import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd-v5';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, Select, Table, Modal, Form, TreeSelect, Input } from '@/components/index';
import './style.less';
import {
  getCsmMarketConfigListRequest,
  addCsmMarketConfigRequest,
  updateCsmMarketConfigRequest,
  deleteCsmMarketConfigRequest,
} from '@/state/request/csmMarket';
import { getAdminUserBaseListRequest } from '@/state/request/adminUser';
import addIcon from '@/common/images/add.png';
import { useDispatch, useSelector } from 'react-redux';
import { marketList } from '@/state/action/stall';
import { getAllDistrictInfo } from '@/state/action/mergeSet';
import { Tooltip } from 'antd-v5';

const sameUserId = (left, right) => left != null && right != null && String(left) === String(right);

const CsmAreaSetting = () => {
  const dispatch = useDispatch();
  const { marketData, districtInfo } = useSelector((state) => {
    const raw = state.mergeSet.districtInfo;
    const normalizedDistrict = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    return {
      marketData: state.stall.marketData || [],
      districtInfo: normalizedDistrict,
    };
  });
  const [modal, contextHolder] = Modal.useModal();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fullList, setFullList] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [adminUserBaseList, setAdminUserBaseList] = useState([]);
  const [adminUserBaseLoaded, setAdminUserBaseLoaded] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  /** 输入框展示值；searchKeyword 为已生效的筛选关键字（前端过滤） */
  const [memberNameInput, setMemberNameInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  /** 省–市树（去掉区县）；省节点不可选，首项为「全部」 */
  const cityTreeData = useMemo(() => {
    const { mapping } = districtInfo || {};
    const { origins = [] } = mapping || [];
    //去掉区
    if (origins instanceof Array) {
      for (const iterator of origins) {
        for (const iteratorChildren of iterator.children) {
          delete iteratorChildren.children;
        }
      }
    }
    return [{ title: '全部', value: '0', key: '0', children: [] }, ...origins];
  }, [districtInfo]);

  const getCsmMarketConfigList = useCallback(async () => {
    setLoading(true);
    const res = await getCsmMarketConfigListRequest({});
    setLoading(false);
    if (res.code === 200) {
      setFullList(res.data || []);
    }
  }, []);

  const getAdminUserBaseList = useCallback(async () => {
    const res = await getAdminUserBaseListRequest();
    setAdminUserBaseLoaded(true);
    if (res.code === 200) {
      setAdminUserBaseList(res.data || []);
    }
  }, []);

  const dataSource = useMemo(() => {
    const kw = String(searchKeyword || '')
      .trim()
      .toLowerCase();
    if (!kw) return fullList;
    return fullList.filter((row) =>
      String(row.name || '')
        .toLowerCase()
        .includes(kw),
    );
  }, [fullList, searchKeyword]);

  useEffect(() => {
    getCsmMarketConfigList();
    dispatch(marketList());
    getAdminUserBaseList();
    dispatch(getAllDistrictInfo());
  }, [dispatch, getAdminUserBaseList, getCsmMarketConfigList]);

  useEffect(() => {
    if (!addModalVisible || !editingRecord || !adminUserBaseLoaded) {
      return;
    }
    const currentUserId = form.getFieldValue('userId');
    if (currentUserId !== undefined && currentUserId !== null) {
      return;
    }
    const existUser = adminUserBaseList.some((item) => sameUserId(item.userId, editingRecord.userId));
    if (existUser) {
      form.setFieldsValue({ userId: editingRecord.userId });
    }
  }, [addModalVisible, adminUserBaseList, adminUserBaseLoaded, editingRecord, form]);

  const handleSearchList = () => {
    setSearchKeyword(memberNameInput.trim());
    getCsmMarketConfigList();
  };

  const handleResetSearch = () => {
    setMemberNameInput('');
    setSearchKeyword('');
    getCsmMarketConfigList();
  };
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setAddModalVisible('添加');
  };

  const handleAddModalCancel = () => {
    setAddModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleAddModalOk = async () => {
    try {
      const values = await form.validateFields();
      const API = values.uuid ? updateCsmMarketConfigRequest : addCsmMarketConfigRequest;
      let districtCodeList = values.districtCodeList || [];
      if (districtCodeList?.length && districtCodeList.includes('0')) {
        districtCodeList = ['0'];
      }
      const payload = {
        userId: values.userId,
        marketIdList: values.marketIdList?.length ? values.marketIdList : [],
        districtCodeList: districtCodeList,
      };
      if (values.uuid) {
        payload.uuid = values.uuid;
      }
      const res = await API(payload);
      if (res.code === 200) {
        message.success(values.uuid ? '编辑成功' : '添加成功');
        handleAddModalCancel();
        getCsmMarketConfigList();
      }
    } catch (e) {
      // 校验失败不关闭弹窗
    }
  };

  const handleEdit = (record) => {
    const { districtItemVOList } = record;
    const districtList = districtItemVOList?.length
      ? districtItemVOList.map((item) => item.districtCode)
      : [];
    const existUser = adminUserBaseList.some((item) => sameUserId(item.userId, record.userId));
    setEditingRecord(record);
    form.setFieldsValue({
      uuid: record.uuid,
      userId: existUser ? record.userId : undefined,
      districtCodeList: districtList,
      marketIdList: record.marketItemVOList?.length
        ? record.marketItemVOList.map((item) => item.marketId)
        : [],
    });
    setAddModalVisible('编辑');
  };

  const handleDelete = async (record) => {
    const res = await deleteCsmMarketConfigRequest({ uuid: record.uuid });
    if (res.code === 200) {
      message.success('删除成功');
      getCsmMarketConfigList();
    }
  };

  const columns = useMemo(
    () => [
      {
        title: '序号',
        key: 'index',
        width: 80,
        render: (_, __, index) => index + 1,
      },
      {
        title: '成员名称',
        dataIndex: 'name',
        key: 'name',
        width: 140,
      },
      {
        title: '手机号',
        dataIndex: 'phone',
        key: 'phone',
        width: 140,
      },
      {
        title: '城市',
        dataIndex: 'districtItemVOList',
        key: 'districtItemVOList',
        ellipsis: true,
        render: (text) => {
          if (!text?.length) {
            return '--';
          }
          const textTip = text
            .map((item) => {
              if (item.districtCode === '0' || item.districtCode === 0) {
                return '全部';
              }
              return item.districtName;
            })
            .join('、');
          return (
            <Tooltip title={textTip} placement='topLeft'>
              <span className='text-ellipsis-2'>{textTip}</span>
            </Tooltip>
          );
        },
      },
      {
        title: '负责市场',
        dataIndex: 'marketItemVOList',
        key: 'marketItemVOList',
        ellipsis: true,
        render: (text) => {
          if (!text?.length) {
            return '--';
          }
          const textTip = text
            .map((item) => {
              if (item.marketId === 0) {
                return '全部';
              }
              return item.marketName;
            })
            .join('、');
          return (
            <Tooltip title={textTip} placement='topLeft'>
              <span className='text-ellipsis-2'>{textTip}</span>
            </Tooltip>
          );
        },
      },
      {
        title: '操作',
        key: 'action',
        width: 120,
        render: (_, record) => (
          <span className='csm-area-actions'>
            <span className='csm-area-edit' onClick={() => handleEdit(record)}>
              编辑
            </span>
            <span
              className='csm-area-delete'
              onClick={() => {
                modal.confirm({
                  className: 'ui-modal',
                  title: '提示',
                  icon: null,
                  content: '确定删除该成员吗？',
                  okText: '删除',
                  okType: 'danger',
                  onOk: () => {
                    handleDelete(record);
                  },
                });
              }}
            >
              删除
            </span>
          </span>
        ),
      },
    ],
    [adminUserBaseList, modal],
  );

  const extraContent = (
    <Button type='primary' onClick={handleAdd}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src={addIcon} style={{ width: 16, height: 16, marginRight: 4 }} alt='add' />
        添加
      </div>
    </Button>
  );

  return (
    <PageHeaderLayout>
      <div className='csm-area-setting-container'>
        <Card bordered={false} bodyStyle={{ padding: '16px 24px' }}>
          <div className='csm-area-setting-header'>
            <div>成员列表</div>
            {extraContent}
          </div>
          <div className='csm-area-search'>
            <Input
              allowClear
              placeholder='请输入成员名称'
              style={{ width: 200 }}
              value={memberNameInput}
              onChange={(e) => {
                const v = e.target.value;
                setMemberNameInput(v);
                if (!v) {
                  setSearchKeyword('');
                }
              }}
              onPressEnter={handleSearchList}
            />
            <Button type='primary' onClick={handleSearchList}>
              查询
            </Button>
            <Button onClick={handleResetSearch}>重置</Button>
          </div>
          <Table rowKey='uuid' loading={loading} columns={columns} dataSource={dataSource} />
        </Card>

        <Modal
          title={addModalVisible}
          open={!!addModalVisible}
          onCancel={handleAddModalCancel}
          footer={
            <div className='csm-area-modal-footer'>
              <Button onClick={handleAddModalCancel}>取消</Button>
              <Button type='primary' className='csm-area-save-btn' onClick={handleAddModalOk}>
                保存
              </Button>
            </div>
          }
          destroyOnClose
          width={520}
          maskClosable={false}
        >
          <Form
            form={form}
            style={{ padding: '12px 0', marginBottom: -16 }}
            labelCol={{ flex: '90px' }}
            wrapperCol={{ flex: '390px' }}
            colon={false}
          >
            <Form.Item name='uuid' hidden></Form.Item>
            <Form.Item
              name='userId'
              label='姓名'
              rules={[{ required: true, message: '选择成员' }]}
            >
              <Select
                style={{ width: '100%' }}
                placeholder='选择成员'
                allowClear
                options={adminUserBaseList.map((m) => ({
                  label: `${m.name} ${m.phone}`,
                  value: m.userId,
                }))}
                showSearch
                optionFilterProp='label'
              />
            </Form.Item>
            <Form.Item
              name='districtCodeList'
              label='选择城市'
              rules={[{ required: true, message: '请选择城市' }]}
            >
              <TreeSelect
                style={{ width: '100%' }}
                allowClear
                showSearch
                multiple
                maxTagCount={5}
                treeData={cityTreeData}
                placeholder='选择城市'
                treeDefaultExpandAll={false}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                filterTreeNode={(input, treeNode) => {
                  return (treeNode?.title ?? '').toString().indexOf(input) >= 0;
                }}
              />
            </Form.Item>
            <Form.Item name='marketIdList' label='负责市场'>
              <Select
                style={{ width: '100%' }}
                placeholder='选择负责市场（可选）'
                allowClear
                multiple
                options={[
                  { label: '全部', value: 0 },
                  ...marketData.map((item) => ({
                    label: item.name,
                    value: item.id,
                  })),
                ]}
                showSearch
                optionFilterProp='label'
                maxTagCount={5}
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
      {contextHolder}
    </PageHeaderLayout>
  );
};

export default CsmAreaSetting;
